import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Typography } from '@mui/material';
import { ShowChart } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { brand } from '../portalTheme';
import { DetailRow, EmptyState, ErrorState, LoadingState, PageTitle, StatusChip, useAsync } from '../PortalUi';
import { CustomerDebit, fetchAllDebits } from '../../../../services/request/CustomerPortal';
import { formatDate, inr, ordinalDay } from '../../../../utils/customerPortalFormat';
import { useTranslation } from '../../../../i18n';

export default function PortalDebits() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, term } = useTranslation();
  const debits = useAsync(fetchAllDebits, []);
  const [selected, setSelected] = useState<CustomerDebit | null>(null);

  useEffect(() => {
    const openDebitId = (location.state as { openDebitId?: string } | null)?.openDebitId;
    if (openDebitId && debits.data) {
      setSelected(debits.data.find(debit => debit.id === openDebitId) ?? null);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [debits.data, location.pathname, location.state, navigate]);

  const debitName = (debit: CustomerDebit) => term('debitType', debit.type, t('portal.common.debit'));
  const selectedDay = ordinalDay(selected?.paymentDate);

  return (
    <Box>
      <PageTitle kicker={t('portal.debits.kicker')} title={t('portal.debits.title')} subtitle={t('portal.debits.subtitle')} />
      {debits.loading && !debits.data ? <LoadingState label={t('portal.debits.loading')} /> : null}
      {debits.error ? <ErrorState message={debits.error} onRetry={debits.reload} /> : null}
      {debits.data && debits.data.length === 0 ? <EmptyState title={t('portal.debits.empty')} /> : null}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
        {(debits.data ?? []).map(debit => {
          const day = ordinalDay(debit.paymentDate);
          return (
            <Paper
              key={debit.id}
              component="button"
              type="button"
              onClick={() => setSelected(debit)}
              sx={{
                p: 3,
                textAlign: 'left',
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
                bgcolor: brand.white,
                transition: 'border-color 0.2s, transform 0.2s',
                '&:hover': { borderColor: brand.gold, transform: 'translateY(-2px)' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6">{debitName(debit)}</Typography>
                <StatusChip status={debit.status} />
              </Box>
              <Typography color="text.secondary">{t('portal.debits.outstanding')}</Typography>
              <Typography variant="h5" sx={{ mb: 1 }}>
                {inr(debit.outstanding)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {day ? t('portal.common.interestDateEachMonth', { day }) : t('portal.common.interestDateMissing')}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        {selected ? (
          <>
            <DialogTitle sx={{ pb: 0 }}>
              <Typography variant="overline" sx={{ color: brand.gold, display: 'block' }}>
                {t('portal.common.debit')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h5" component="span">
                  {debitName(selected)}
                </Typography>
                <StatusChip status={selected.status} />
              </Box>
            </DialogTitle>
            <DialogContent>
              <DetailRow label={t('portal.debits.fields.outstanding')} value={inr(selected.outstanding)} />
              <DetailRow label={t('portal.debits.fields.originalAmount')} value={inr(selected.amount)} />
              <DetailRow
                label={t('portal.debits.fields.interestRate')}
                value={
                  typeof selected.interestRate === 'number' && Number.isFinite(selected.interestRate)
                    ? t('portal.common.ratePerMonth', { value: selected.interestRate })
                    : undefined
                }
              />
              <DetailRow
                label={t('portal.debits.fields.interestDate')}
                value={selectedDay ? t('portal.common.dayOfMonth', { day: selectedDay }) : undefined}
              />
              <DetailRow label={t('portal.debits.fields.lastInterest')} value={formatDate(selected.lastInterestCollectedDate)} />
              <DetailRow
                label={t('portal.debits.fields.duration')}
                value={selected.durationType ? term('duration', selected.durationType) : undefined}
              />
              <DetailRow label={t('portal.debits.fields.startDate')} value={formatDate(selected.startDate)} />
              <DetailRow label={t('portal.debits.fields.maturityDate')} value={formatDate(selected.endDate)} />
              <DetailRow label={t('portal.debits.fields.instalment')} value={selected.emiAmount ? inr(selected.emiAmount) : undefined} />
              <DetailRow label={t('portal.debits.fields.closed')} value={formatDate(selected.closedAt)} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexWrap: 'wrap' }}>
              <Button onClick={() => setSelected(null)}>{t('common.close')}</Button>
              <Button
                variant="contained"
                startIcon={<ShowChart />}
                onClick={() => navigate(`/customer/debits/${encodeURIComponent(selected.id)}/payments`)}
              >
                {t('portal.debits.viewPayments')}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Box>
  );
}
